// Ask a super_admin to make a change, instead of making it.
//
// One dialog for both entities and both actions. The reason is mandatory and
// the field is the whole point: the reviewer sees this sentence and the diff,
// nothing else, so "please" tells them nothing and they will reject it.
//
// What is being changed is shown before the box, not summarised afterwards -
// somebody asking for a deletion should see the record's name while they type
// the justification for removing it.
import { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';

export interface ChangeRequestDraft {
  tableName: 'drivers' | 'vehicles';
  recordId: string;
  /** What the reviewer will see this request is about, e.g. "DRV-0042 · Amir". */
  label: string;
  action: 'update' | 'delete';
  /** Only for an update: the fields that actually differ. */
  fields?: Record<string, unknown>;
}

export function RequestChangeDialog({ draft, onClose, onSent }: {
  draft: ChangeRequestDraft | null;
  onClose: () => void;
  onSent?: (msg: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!draft) return null;

  const entries = Object.entries(draft.fields ?? {});

  const send = async () => {
    if (reason.trim().length < 3) { setError('Say why — the reviewer only has this.'); return; }
    setBusy(true); setError(null);
    try {
      await apiClient.post('/api/v1/change-requests', {
        tableName: draft.tableName,
        recordId: draft.recordId,
        action: draft.action,
        reason: reason.trim(),
        fields: draft.action === 'update' ? draft.fields : undefined,
      });
      onSent?.(`Sent to the super admin. You will see it under ${draft.label}.`);
      setReason('');
      onClose();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!draft} onOpenChange={(o) => { if (!o) { setReason(''); setError(null); onClose(); } }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {draft.action === 'delete' ? 'Request deletion' : 'Request these changes'}
          </SheetTitle>
          <SheetDescription>
            Only a super admin can {draft.action === 'delete' ? 'delete' : 'change'} this.
            Your request goes to them with the reason you give.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-sm font-medium text-foreground">{draft.label}</p>
            {draft.action === 'delete' ? (
              <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                To be deleted.
              </p>
            ) : entries.length ? (
              <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                {entries.map(([k, v]) => (
                  <div key={k}>
                    <span className="font-mono">{k}</span> →{' '}
                    <b className="text-foreground">{v === null || v === '' ? '(cleared)' : String(v)}</b>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">Nothing changed.</p>
            )}
          </div>

          <div>
            <label className="text-sm text-foreground" htmlFor="cr-reason">
              Why {/* the reviewer sees this and the diff, nothing else */}
            </label>
            <textarea
              id="cr-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              autoFocus
              placeholder={draft.action === 'delete'
                ? 'e.g. duplicate record created by the March import'
                : 'e.g. iqama corrected from the renewed card'}
              className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2 text-sm">{error}</div>
          )}

          <div className="flex gap-2">
            <Button
              variant="gold"
              disabled={busy || (draft.action === 'update' && entries.length === 0)}
              onClick={() => void send()}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send request
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The fields that actually differ, so a request carries the change rather than
 * the whole form. A reviewer approving "everything, unchanged" learns nothing
 * and would rubber-stamp it.
 */
export function diffFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(after)) {
    const b = before[k];
    const same = (b ?? null) === (v ?? null)
      || (b instanceof Date ? b.toISOString().slice(0, 10) : String(b ?? '')) === String(v ?? '');
    if (!same) out[k] = v;
  }
  return out;
}

export default RequestChangeDialog;
